import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { localePath } from "@/i18n/localePath";
import { ScrollReveal } from "./ScrollEffects";
import { ArrowRight, Sparkles, Check } from "lucide-react";

const plans = [
  { key: "free", popular: false },
  { key: "plus", popular: true },
  { key: "forever", popular: false },
] as const;

export const PricingTeaser: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lp = (path: string) => localePath(path, i18n.language);

  return (
    <section className="py-20 sm:py-28 px-4 relative overflow-hidden" id="pricing-teaser">
      <div className="absolute inset-0 bg-muted/30" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      <div className="max-w-4xl mx-auto relative z-10">
        <ScrollReveal className="text-center mb-12">
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-5">
            {t('pricing_teaser.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4">
            {t('pricing_teaser.title')}
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            {t('pricing_teaser.subtitle')}
          </p>
        </ScrollReveal>

        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mb-8">
          {plans.map((plan, i) => {
            const isPopular = plan.popular;
            return (
              <motion.div
                key={plan.key}
                className={`relative rounded-3xl p-6 sm:p-7 border transition-all duration-300 ${
                  isPopular
                    ? "bg-card border-primary/30 shadow-lg ring-1 ring-primary/10"
                    : "bg-card/80 border-border/40"
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -6, boxShadow: isPopular
                  ? "0 20px 50px -12px hsl(var(--primary) / 0.2)"
                  : "0 12px 30px -8px hsl(var(--foreground) / 0.06)"
                }}
              >
                {isPopular && (
                  <motion.div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center gap-1"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 300, delay: 0.3 }}
                  >
                    <Sparkles size={10} />
                    Popular
                  </motion.div>
                )}

                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                    {t(`pricing_teaser.${plan.key}_label`)}
                  </p>
                  <p className="text-3xl sm:text-4xl font-black text-foreground mb-1">
                    {t(`pricing_teaser.${plan.key}_price`)}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {t(`pricing_teaser.${plan.key}_desc`)}
                  </p>
                  
                  {/* Visual checkmark */}
                  <div className="flex justify-center">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check size={14} className="text-primary" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <ScrollReveal className="text-center">
          <motion.button
            onClick={() => navigate(lp("/pricing"))}
            className="inline-flex items-center gap-2 text-primary font-bold text-sm hover:text-primary/80 transition-colors px-4 py-2 rounded-xl hover:bg-primary/5"
            whileHover={{ x: 4 }}
          >
            {t('pricing_teaser.see_all')}
            <ArrowRight size={16} />
          </motion.button>
        </ScrollReveal>
      </div>
    </section>
  );
};
